import React from 'react';
import { Icon } from '@eventbrite/marmalade';
import type { SectionBuilder } from '../Section';
import { CardSize } from '../../types';
import type { Highlights } from '../../types';
import { SectionComplexView } from '../SectionComplexView';
import { SectionCardView } from '../SectionCardView';
import { formatDuration } from './formatDuration';
import { Duration, IDCard, Parking, Globe, InPerson, DoorsOpen } from '../../icons';
import { useTranslation } from '../../context/TranslationContext/TranslationContext';

import styles from './Highlights.module.scss';

export const DurationHighlight = ({ duration, durationString }: Highlights) => {
  let durationToUse = durationString;

  if (!durationString && duration) {
    durationToUse = formatDuration(duration);
  }

  if (!durationToUse) {
    return null;
  }

  return (
    <>
      <Icon size="xsmall">
        <Duration />
      </Icon>
      {durationToUse}
    </>
  );
};

export const AgeRestrictionHighlight = ({ age_restriction }: Highlights) => {
  if (!age_restriction) {
    return null;
  }
  return (
    <>
      <Icon size="xsmall">
        <IDCard />
      </Icon>
      <span className={styles.ageRestriction}>{age_restriction.toLowerCase()}</span>
    </>
  );
};

export const FormatHighlight = ({ isOnline }: Highlights) => {
  const { online: onlineText = 'Online', inPerson: inPersonText = 'In person' } = useTranslation();
  return (
    <>
      {isOnline ? (
        <Icon size="xsmall">
          <Globe />
        </Icon>
      ) : (
        <Icon size="xsmall">
          <InPerson />
        </Icon>
      )}
      {isOnline ? onlineText : inPersonText}
    </>
  );
};

export const DoorsOpenHighlight = (data: Highlights) => {
  if (!data.doorTime) {
    return null;
  }
  return (
    <>
      <Icon size="xsmall">
        <DoorsOpen />
      </Icon>
      {data.doorTime}
    </>
  );
};

export const ParkingHighlight = (data: Highlights) => {
  const { freeParking: freeParkingText = 'Free venue parking', paidParking: paidParkingText = 'Paid venue parking' } =
    useTranslation();
  const { freeParking, paidParking } = data;
  const parkingText = freeParking ? freeParkingText : paidParking ? paidParkingText : null;

  if (!parkingText) {
    return null;
  }

  return (
    <>
      <Icon size="xsmall">
        <Parking />
      </Icon>
      {parkingText}
    </>
  );
};

type HighlightsObject = {
  condition: (data: Highlights) => boolean;
  builderFn: (data: Highlights) => React.ReactNode;
  name: string;
};

const BuildHighlights = (data: Highlights) => {
  const highlights: HighlightsObject[] = [
    {
      condition: () => true,
      builderFn: (data) => DurationHighlight(data),
      name: 'Duration',
    },
    {
      condition: (data) => !!data.age_restriction,
      builderFn: (data) => AgeRestrictionHighlight(data),
      name: 'Age restriction',
    },
    {
      condition: () => true,
      builderFn: (data) => FormatHighlight(data),
      name: 'Format',
    },
    {
      condition: (data) => !data.isOnline,
      builderFn: (data) => ParkingHighlight(data),
      name: 'Parking',
    },
    {
      condition: (data) => !!data.doorTime,
      builderFn: (data) => DoorsOpenHighlight(data),
      name: 'Doors open',
    },
  ];

  return (
    <ul className={styles.wrapper}>
      {highlights.map((highlight) => {
        if (highlight.condition(data)) {
          return (
            <li key={highlight.name} className={styles.highlightItem}>
              {highlight.builderFn(data)}
            </li>
          );
        }
        return null;
      })}
    </ul>
  );
};

export const HighlightsCard = ({
  data,
  onReadMoreClick,
  size,
}: {
  data: Highlights;
  onReadMoreClick: () => void;
  size: CardSize;
}) => {
  const { highlights: title = 'Highlights' } = useTranslation();
  return (
    <SectionCardView
      title={title}
      body={BuildHighlights(data)}
      onReadMoreClick={function (): void {
        throw new Error('Function not implemented.');
      }}
      bodyClassName={styles.body}
      size={size}
    ></SectionCardView>
  );
};

export const HighlightsView = ({ data }: { data: Highlights }) => {
  const { highlights: title = 'Highlights' } = useTranslation();
  return <SectionComplexView title={title}>{BuildHighlights(data)}</SectionComplexView>;
};

const HighlightsSection: SectionBuilder<Highlights> = (data: Highlights) => ({
  key: 'highlights',
  CardView: ({ onReadMoreClick, size = CardSize.CONTAINED }) => (
    <HighlightsCard data={data} onReadMoreClick={onReadMoreClick} size={size} />
  ),
  GeneralView: () => <HighlightsView data={data} />,
  SingleView: () => <HighlightsView data={data} />,
});

export default HighlightsSection;
